import { memo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { isCustomTool, getCustomToolId } from '@/config/tools';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { LayoutGrid, ChevronDown, CheckCircle, Search, SlidersHorizontal } from 'lucide-react';

/**
 * ToolHeader Component
 *
 * A standardized header for all tools within a space.
 *
 * @param {Object} props
 * @param {string} props.spaceName - Name of the current space
 * @param {string} props.spaceImage - Image/logo URL for the space or stack
 * @param {string} props.toolName - Name of the current tool (e.g., "Matrix", "Team Management")
 * @param {string} props.currentTool - Current tool type identifier (e.g., "matrix", "team")
 * @param {Array} props.spaceTools - Array of available tools in the space
 * @param {React.ReactNode} props.actions - Right-side action buttons (optional)
 * @param {string} props.stackId - Stack ID (for stack views)
 * @param {Array} props.stackProjects - Projects in the stack (for stack views)
 * @param {boolean} props.isStackView - Whether this is a stack view
 * @param {string} props.selectedSpaceFilter - Selected space filter (for stack views)
 * @param {Function} props.onSpaceFilterChange - Callback when space filter changes (for stack views)
 * @param {Object} props.parentBreadcrumb - Optional parent breadcrumb level (e.g., {name: "Bookings", onClick: () => navigate('/bookings')})
 * @param {Array} props.breadcrumbs - Optional array of breadcrumb items [{name: string, onClick: () => void}] for nested navigation
 * @param {string} props.searchValue - Optional search input value
 * @param {Function} props.onSearchChange - Optional callback when search value changes
 * @param {string} props.searchPlaceholder - Optional placeholder for search input (default: "Search...")
 * @param {React.ReactNode} props.filterContent - Optional content to render inside filters popover
 * @param {boolean} props.showFiltersButton - Whether to show the filters button (default: false)
 * @param {React.ReactNode} props.statusInfo - Optional status indicators to show next to search (e.g., count badges)
 *
 * @example
 * <ToolHeader
 *   spaceName="Project Alpha"
 *   spaceImage="https://example.com/logo.png"
 *   toolName="Training Matrix"
 *   currentTool="matrix"
 *   spaceTools={tools}
 *   actions={
 *     <>
 *       <Button onClick={handleExport}>Export</Button>
 *       <Button onClick={handleAdd}>Add</Button>
 *     </>
 *   }
 * />
 */
export const ToolHeader = memo(({
  spaceName,
  spaceImage,
  toolName,
  currentTool,
  spaceTools = [],
  actions = null,
  stackId = null,
  stackProjects = [],
  isStackView = false,
  selectedSpaceFilter = 'all',
  onSpaceFilterChange = null,
  parentBreadcrumb = null,
  isGlobalView = false,
  // New props for extended functionality
  breadcrumbs = null,
  searchValue = null,
  onSearchChange = null,
  searchPlaceholder = 'Search...',
  filterContent = null,
  showFiltersButton = false,
  statusInfo = null
}) => {
  const navigate = useNavigate();
  const { projectId, stackId: paramStackId } = useParams();
  const activeStackId = stackId || paramStackId;

  // Tool route mapping
  const getToolRoute = (toolType, tool = null) => {
    // Handle custom tools
    if (isCustomTool(toolType)) {
      const customToolId = getCustomToolId(toolType);
      const slug = tool?.customToolData?.slug || customToolId;
      if (projectId) {
        return `/space/${projectId}/tool/${slug}`;
      }
      return '/dashboard';
    }

    if (isGlobalView) {
      // Global routes
      const globalRoutes = {
        spaces: '/dashboard',
        home: '/dashboard',
        team: '/company-team',
        users: '/company-team',
        qualifications: '/qualifications',
        trainers: '/trainers',
        attendance: '/attendance',
        message_board: '/posts',
        posts: '/posts',
        processes: '/processes',
      };
      return globalRoutes[toolType] || '/dashboard';
    }

    if (isStackView && activeStackId) {
      const stackRoutes = {
        matrix: `/stack/${activeStackId}/matrix`,
        team: `/stack/${activeStackId}/team`,
        qualifications: `/stack/${activeStackId}/qualifications`,
        bookings: `/stack/${activeStackId}/bookings`,
        calendar: `/stack/${activeStackId}/schedule`,
        tasks: `/stack/${activeStackId}/tasks`,
        checklists: `/stack/${activeStackId}/tasks`,
        docs: `/stack/${activeStackId}/docs`,
        message_board: `/stack/${activeStackId}/message-board`,
        check_ins: `/stack/${activeStackId}/checkins`,
        checkins: `/stack/${activeStackId}/checkins`,
      };
      return stackRoutes[toolType] || `/stack/${activeStackId}`;
    }

    const routes = {
      matrix: `/space/${projectId}/matrix`,
      team: `/space/${projectId}/team`,
      compliance: `/space/${projectId}/compliance`,
      team_compliance: `/space/${projectId}/compliance`,
      site_compliance: `/space/${projectId}/site-compliance`,
      bookings: `/space/${projectId}/bookings`,
      qualifications: `/space/${projectId}/qualifications`,
      chat: `/chat?space=${projectId}`,
      message_board: `/space/${projectId}/message-board`,
      check_ins: `/space/${projectId}/checkins`,
      checkins: `/space/${projectId}/checkins`,
      calendar: `/space/${projectId}/schedule`,
      tasks: `/space/${projectId}/tasks`,
      checklists: `/space/${projectId}/tasks`,
      docs: `/space/${projectId}/docs`,
    };
    return routes[toolType] || `/space/${projectId}`;
  };

  const handleToolClick = (toolType, event, tool = null) => {
    if (toolType === currentTool) {
      return; // Already on this tool
    }

    const route = getToolRoute(toolType, tool);

    // Check if ctrl/cmd + click for new tab
    if (event.ctrlKey || event.metaKey) {
      window.open(route, '_blank');
    } else {
      navigate(route);
    }
  };

  const handleToolAuxClick = (toolType, event, tool = null) => {
    // Handle middle click (mouse wheel click)
    if (event.button === 1) {
      event.preventDefault();

      if (toolType === currentTool) {
        return;
      }

      const route = getToolRoute(toolType, tool);
      window.open(route, '_blank');
    }
  };

  return (
    <div className="w-full bg-white border-b border-gray-200">
      <div className="flex items-center justify-between py-3 px-6">
        {/* Left side - Tool switcher + Breadcrumb */}
        <div className="flex items-center gap-3">
          {/* Tools Dropdown */}
          <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 px-2 flex items-center gap-1 border border-gray-200 bg-background hover:bg-muted data-[state=open]:bg-muted data-[state=open]:border-gray-300 rounded-md"
                >
                  <LayoutGrid className="h-4 w-4 text-gray-600" />
                  <ChevronDown className="h-3 w-3 text-gray-500 data-[state=open]:rotate-180 transition-transform" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                sideOffset={8}
                className="w-[400px] p-4 rounded-lg border border-gray-200 shadow-lg bg-white"
                onCloseAutoFocus={(e) => e.preventDefault()}
              >
                <div className="grid grid-cols-3 gap-3">
                  {spaceTools.map((tool) => {
                    const Icon = tool.icon;
                    const isActive = tool.tool_type === currentTool;

                    // Skip tools without valid icons
                    // Accept both function components and React.forwardRef components
                    if (!Icon) {
                      console.warn('[ToolHeader] No icon provided for tool:', tool.tool_type);
                      return null;
                    }

                    // Validate it's a renderable React component (function or forwardRef)
                    const isValidComponent = typeof Icon === 'function' || (typeof Icon === 'object' && Icon.$$typeof);
                    if (!isValidComponent) {
                      console.warn('[ToolHeader] Invalid icon for tool:', tool.tool_type, Icon);
                      return null;
                    }

                    return (
                      <div
                        key={tool.id}
                        className={`relative flex flex-col items-center p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                          isActive ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                        onClick={(e) => handleToolClick(tool.tool_type, e, tool)}
                        onAuxClick={(e) => handleToolAuxClick(tool.tool_type, e, tool)}
                      >
                        <div
                          className="w-12 h-12 rounded-lg flex items-center justify-center mb-2 flex-shrink-0"
                          style={{ backgroundColor: tool.color + '20' }}
                        >
                          <Icon
                            className="h-6 w-6"
                            style={{ color: tool.color }}
                          />
                        </div>
                        <div className="h-8 flex items-center justify-center">
                          <span className="text-xs font-medium text-center line-clamp-2 w-full leading-tight">
                            {tool.name}
                          </span>
                        </div>
                        {isActive && (
                          <div className="absolute top-2 right-2">
                            <CheckCircle className="h-4 w-4 text-blue-600" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {spaceTools.length === 0 && (
                    <div className="col-span-3 text-sm text-gray-500 text-center py-4">
                      No tools configured
                    </div>
                  )}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

          {/* Breadcrumb */}
          <div className="flex items-center gap-2.5">
            {!isGlobalView && (
              // Space/Stack view - show space name and separator
              <>
              <button
                onClick={() => navigate(isStackView ? `/stack/${activeStackId}` : `/space/${projectId}`)}
                className="flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
              >
                {spaceImage && (
                  <img
                    src={spaceImage}
                    alt={spaceName}
                    className="w-5 h-5 rounded object-cover"
                  />
                )}
                <span>{spaceName}</span>
              </button>
              <span className="text-sm text-gray-300">/</span>
              </>
            )}
            {parentBreadcrumb ? (
              <>
                <button
                  onClick={parentBreadcrumb.onClick}
                  className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
                >
                  {parentBreadcrumb.name}
                </button>
                <span className="text-sm text-gray-300">/</span>
                {/* If we have additional breadcrumbs, toolName becomes clickable */}
                {breadcrumbs && breadcrumbs.length > 0 ? (
                  <button
                    onClick={breadcrumbs[0]?.onToolClick}
                    className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {toolName}
                  </button>
                ) : (
                  <h1 className="text-sm font-semibold text-gray-700">{toolName}</h1>
                )}
              </>
            ) : (
              // If we have additional breadcrumbs, toolName becomes clickable
              breadcrumbs && breadcrumbs.length > 0 ? (
                <button
                  onClick={breadcrumbs[0]?.onToolClick}
                  className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
                >
                  {toolName}
                </button>
              ) : (
                <h1 className="text-sm font-semibold text-gray-700">{toolName}</h1>
              )
            )}
            {/* Additional breadcrumbs from the breadcrumbs array */}
            {breadcrumbs && breadcrumbs.map((crumb, index) => (
              <span key={index} className="flex items-center gap-2.5">
                <span className="text-sm text-gray-300">/</span>
                {index === breadcrumbs.length - 1 ? (
                  // Last breadcrumb is the current location - not clickable
                  <h1 className="text-sm font-semibold text-gray-700">{crumb.name}</h1>
                ) : (
                  // Intermediate breadcrumbs are clickable
                  <button
                    onClick={crumb.onClick}
                    className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {crumb.name}
                  </button>
                )}
              </span>
            ))}
          </div>
        </div>

        {/* Right side - Search, Filters, Space Filter + Dynamic actions */}
        <div className="flex items-center gap-2">
          {/* Status Info (count badges) */}
          {statusInfo}

          {/* Search Input */}
          {onSearchChange && (
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder={searchPlaceholder}
                value={searchValue || ''}
                onChange={(e) => onSearchChange(e.target.value)}
                className="h-8 w-56 pl-9 text-sm bg-white"
              />
            </div>
          )}

          {/* Filters Popover */}
          {showFiltersButton && filterContent && (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 px-3 gap-1.5">
                  <SlidersHorizontal className="h-4 w-4" />
                  <span className="text-sm">Filters</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-64 p-4">
                {filterContent}
              </PopoverContent>
            </Popover>
          )}

          {/* Space Filter Dropdown - Only show for stack view */}
          {isStackView && stackProjects && stackProjects.length > 0 && onSpaceFilterChange && (
            <Select value={selectedSpaceFilter} onValueChange={onSpaceFilterChange}>
              <SelectTrigger className="w-[200px] h-8 text-sm bg-white">
                <SelectValue placeholder="Filter by space" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Spaces ({stackProjects.length})</SelectItem>
                {stackProjects.map(project => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {actions}
        </div>
      </div>
    </div>
  );
});

ToolHeader.displayName = 'ToolHeader';
